import React from "react";
import { Link, NavLink } from "react-router-dom";
import { useLang } from "@/lib/i18n-context";
import { useAuth } from "@/lib/auth-context";
import { LANGUAGES } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Globe, LogIn, LogOut, User as UserIcon, Menu } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const navItems = [
  { key: "nav_home", to: "/", testid: "nav-home" },
  { key: "nav_map", to: "/map", testid: "nav-map" },
  { key: "nav_booths", to: "/booth-info", testid: "nav-booths" },
  { key: "nav_gallery", to: "/gallery", testid: "nav-gallery" },
  { key: "nav_sponsors", to: "/sponsors", testid: "nav-sponsors" },
  { key: "nav_register", to: "/register", testid: "nav-register" },
];

export default function Header() {
  const { lang, setLang, t } = useLang();
  const { user, login, logout } = useAuth();
  const current = LANGUAGES.find((l) => l.code === lang);

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-border/70"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between h-16 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img src="/ijff_logo.png" alt="IJFF 2026" className="h-11 md:h-12 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-brand-red text-white"
                    : "text-foreground hover:bg-brand-canvas"
                }`
              }
            >
              {t(n.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 h-9"
                data-testid="lang-switcher"
              >
                <Globe className="w-4 h-4" />
                <span className="font-semibold">{current?.label}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {LANGUAGES.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`lang-${l.code}`}
                  className={lang === l.code ? "bg-brand-canvas font-semibold" : ""}
                >
                  <span className="mr-2 font-mono text-xs">{l.label}</span>
                  {l.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 h-9 rounded-full"
                  data-testid="user-menu-trigger"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <UserIcon className="w-4 h-4" />
                  )}
                  <span className="hidden md:inline max-w-[120px] truncate">
                    {user.name || user.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/dashboard" data-testid="menu-dashboard">
                    {t("nav_dashboard")}
                  </Link>
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" data-testid="menu-admin">
                      <span className="text-brand-red font-semibold">Admin Panel</span>
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={login}
              size="sm"
              className="h-9 rounded-full bg-brand-red hover:bg-brand-red-dark text-white gap-2"
              data-testid="header-login-btn"
            >
              <LogIn className="w-4 h-4" />
              <span className="hidden sm:inline">{t("login")}</span>
            </Button>
          )}

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle className="font-display">IJFF 2026</SheetTitle>
              </SheetHeader>
              <div className="mt-8 flex flex-col gap-2">
                {navItems.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`m-${n.testid}`}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-lg font-medium ${
                        isActive ? "bg-brand-red text-white" : "hover:bg-brand-canvas"
                      }`
                    }
                  >
                    {t(n.key)}
                  </NavLink>
                ))}
                {user && (
                  <NavLink
                    to="/dashboard"
                    data-testid="m-nav-dashboard"
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-lg font-medium ${
                        isActive ? "bg-brand-red text-white" : "hover:bg-brand-canvas"
                      }`
                    }
                  >
                    {t("nav_dashboard")}
                  </NavLink>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
